import { toast } from '@/components/toast';
import {
    deleteExternalKey,
    deleteSecret,
    getExternalKey,
    getSecretById,
    getSecrets,
    upsertExternalKey,
    upsertSecret,
} from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { ALL_VOICES_ID } from '@/state/presenters';
import { ExternalKeysProviders, GetExternalKeysResponseDto, SanitizedSecretDto } from '@d-id/types';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';

const getExternalKeyQueryKey = (provider: ExternalKeysProviders) => `external-key-${provider}`;
const getSecretsQueryKey = (provider: ExternalKeysProviders) => `secrets-${provider}`;
const getSecretByIdQueryKey = (id: string) => `secret-${id}`;

export function useUpsertExternalKey(refetchVoices?: () => Promise<void>) {
    const queryClient = useQueryClient();

    return useMutation(upsertExternalKey, {
        onSuccess: async (_, data) => {
            await queryClient.resetQueries({ queryKey: [getExternalKeyQueryKey(data.provider)] });
            await queryClient.invalidateQueries([ALL_VOICES_ID]);
            await refetchVoices?.();
            toast.success({ title: 'Voices added successfully', toastId: `upsert-external-key-success` });
        },
        onError: () => {
            toast.error({ title: 'Error during saving API key', toastId: `upsert-external-key-error` });
        },
    });
}

export function useDeleteExternalKey(refetchVoices?: () => Promise<void>) {
    const queryClient = useQueryClient();

    return useMutation(deleteExternalKey, {
        onSuccess: async (_, provider) => {
            await queryClient.resetQueries({ queryKey: [getExternalKeyQueryKey(provider)] });
            await queryClient.invalidateQueries([ALL_VOICES_ID]);
            await refetchVoices?.();
            toast.success({ title: 'Deleted API key', toastId: `delete-external-key-success` });
        },
        onError: () => {
            toast.error({ title: 'Error during deleting API key', toastId: `delete-external-key-error` });
        },
    });
}

export function useGetExternalKey(provider: ExternalKeysProviders): UseQueryResult<GetExternalKeysResponseDto | null> {
    const { isAnonymous = true } = useAuth();

    return useQuery<GetExternalKeysResponseDto>(getExternalKeyQueryKey(provider), () => getExternalKey(provider), {
        enabled: !isAnonymous,
        retry: false,
        onError(error) {
            if (!isAnonymous) {
                logger.error('Error during getting API key', { error });
            }
        },
    });
}

export function useGetSecrets(provider: ExternalKeysProviders): UseQueryResult<GetExternalKeysResponseDto[]> {
    const { isAnonymous = true } = useAuth();

    return useQuery<GetExternalKeysResponseDto[]>(getSecretsQueryKey(provider), () => getSecrets(provider), {
        enabled: !isAnonymous,
        retry: false,
    });
}

export function useUpsertSecret(toastMessage?: string) {
    const queryClient = useQueryClient();

    return useMutation(upsertSecret, {
        onSuccess: async (_, data) => {
            await queryClient.invalidateQueries({ queryKey: [getSecretsQueryKey(data.provider)] });
            toastMessage && toast.success({ title: toastMessage, toastId: `upsert-secret-success` });
        },
        onError: () => {
            toast.error({ title: 'Error during saving API key', toastId: `upsert-secret-error` });
        },
    });
}

export function useDeleteSecret(toastMessage: string, provider: ExternalKeysProviders) {
    const queryClient = useQueryClient();

    return useMutation(deleteSecret, {
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [getSecretsQueryKey(provider)] });
            toast.success({ title: toastMessage, toastId: `delete-secret-success` });
        },
        onError: () => {
            toast.error({ title: 'Error during deleting API key', toastId: `delete-secret-error` });
        },
    });
}

export function useGetSecretById(id: string | undefined, isEnabled: boolean = true): UseQueryResult<SanitizedSecretDto | null> {
    const { isAnonymous = true } = useAuth();

    return useQuery<SanitizedSecretDto | null>(getSecretByIdQueryKey(id || ''), () => (id ? getSecretById(id) : Promise.resolve(null)), {
        enabled: !isAnonymous && !!id && isEnabled,
        retry: false,
    });
}
